import React from 'react';
import { ADDON_CLEANUP_REGEX } from '../../utils/addonRegex';
import { cleanDescriptionHtml } from '../../utils/cleanHtml';
import { isCategoryTaxable, isServiceCurrentlyAvailable, validOfferedDurations } from '../../utils/categories';

export default function ServicesColumn({
  dataLoading,
  categories,
  activeServices,
  activePractitioners,
  selectedCategory,
  setSelectedCategory,
  selectedService,
  setSelectedService,
  setSelectedPractitioner,
  setActivePracTab,
  setMainBgColor,
  primaryServicesForCategory,
  addonServicesForCategory,
  includeSalesTax,
  setIncludeSalesTax,
  appSalesTaxRate,
  serviceInfoFontSizeLevel,
  increaseFontSize,
  decreaseFontSize,
  getServiceInfoFontSizeClass,
  adminMode,
  handleOpenEditServiceModal,
  playClickSound,
  defaults,
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold border-b pb-2">Services</h2>
      {dataLoading ? (
        <div className="space-y-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-24" />
          <div className="h-11 bg-gray-200 rounded-xl" />
          <div className="h-4 bg-gray-200 rounded w-20" />
          <div className="h-11 bg-gray-200 rounded-xl" />
          <div className="mt-4 p-4 border-2 border-gray-200 rounded-lg space-y-3">
            <div className="h-5 bg-gray-200 rounded w-40" />
            <div className="h-4 bg-gray-100 rounded" />
            <div className="h-4 bg-gray-100 rounded w-5/6" />
            <div className="h-4 bg-gray-100 rounded w-4/6" />
          </div>
        </div>
      ) : (<>
      <p className="text-base font-semibold mb-2">Category:</p>
      <select className="w-full p-3 border rounded-xl bg-white shadow-sm" value={selectedCategory} onChange={e => { playClickSound(); const newCategoryName = e.target.value; setSelectedCategory(newCategoryName); setSelectedService(null); setSelectedPractitioner(null); const selectedCatObj = categories.find(cat => cat.name === newCategoryName); if (selectedCatObj) setMainBgColor(selectedCatObj.color); else setMainBgColor(defaults.categoryColor || '#314e4a'); }}>
        <option value="" className="text-gray-500">Select Category</option>
        {[...categories].sort((a, b) => a.name.localeCompare(b.name)).map((cat, i) => (<option key={i} value={cat.name}>{cat.name}</option>))}
      </select>
      <p className="text-base font-semibold mb-2">Service:</p>

      <select className="w-full p-3 border rounded-xl bg-white shadow-sm" value={selectedService?.name || ''} onChange={e => { playClickSound(); const svc = activeServices.find(s => s.name === e.target.value); setSelectedService(svc); }}>
        <option value="" className="text-gray-500">Select Service</option>
        {primaryServicesForCategory.map((svc, i) => (
          <option key={`primary-${i}`} value={svc.name}>{svc.name}</option>
        ))}
        {addonServicesForCategory.length > 0 && (
          <optgroup label="Add-ons">
            {addonServicesForCategory.map((svc, i) => (
              <option key={`addon-${i}`} value={svc.name}>
                {svc.name.replace(ADDON_CLEANUP_REGEX, '').trim()}
              </option>
            ))}
          </optgroup>
        )}
      </select>

      {selectedService && (
        <div className="mt-4 p-4 border-2 border-gray-300 rounded-lg bg-gray-50 shadow-md">
          <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-300">
            <h3 className="text-lg font-medium text-gray-800">Service Information</h3>
            <div className="flex items-center space-x-2">
              {adminMode && (
                <button onClick={() => handleOpenEditServiceModal(selectedService)} className="p-1.5 rounded-md hover:bg-gray-300 text-gray-600" aria-label="Edit Service">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                </button>
              )}
              <button onClick={decreaseFontSize} className="p-1 rounded-md hover:bg-gray-300 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={serviceInfoFontSizeLevel === 0} aria-label="Decrease font size"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" /></svg></button>
              <button onClick={increaseFontSize} className="p-1 rounded-md hover:bg-gray-300 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={serviceInfoFontSizeLevel === 3} aria-label="Increase font size"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg></button>
            </div>
          </div>
          <div className={`${getServiceInfoFontSizeClass()} text-gray-700 whitespace-pre-line space-y-3`}>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Description:</h4>
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: cleanDescriptionHtml(selectedService.description) }} />
            </div>

            <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="space-y-1">
                <p><span className="font-semibold">Currently Available: </span><span className={isServiceCurrentlyAvailable(selectedService) ? "text-green-600 font-bold" : "text-red-600 font-bold"}>{isServiceCurrentlyAvailable(selectedService) ? 'Yes' : 'No'}</span></p>
                {selectedService.category === 'Acupuncture' && (<>
                  <p><span className="font-semibold">Insurance Accepted: </span><span className={selectedService.isInsuranceAccepted ? "text-green-600 font-bold" : "text-red-600 font-bold"}>{selectedService.isInsuranceAccepted ? 'Yes' : 'No'}</span></p>
                </>)}
                {selectedService.category === 'Skin Care' && (<>
                  <p><span className="font-semibold">Extractions Included: </span><span className={selectedService.isExtractionsIncluded ? "text-green-600 font-bold" : "text-red-600 font-bold"}>{selectedService.isExtractionsIncluded ? 'Yes' : 'No'}</span></p>
                  <p><span className="font-semibold">Safe For Prenatal: </span><span className={selectedService.isSafeForPrenatal ? "text-green-600 font-bold" : "text-red-600 font-bold"}>{selectedService.isSafeForPrenatal ? 'Yes' : 'No'}</span></p>
                </>)}
              </div>
            </div>

            {selectedService.durationPrices && selectedService.durationPrices.length > 0 && (
              <div className="mt-4 pt-3 pb-2 px-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-2">Service Prices by Duration</h4>
                {isCategoryTaxable(categories, selectedService.category) && (<label className="inline-flex items-center mb-2"><input type="checkbox" className="form-checkbox h-4 w-4 text-blue-600" checked={includeSalesTax} onChange={() => { playClickSound(); setIncludeSalesTax(!includeSalesTax); }} /><span className="ml-2">Include Tax ({(appSalesTaxRate * 100).toFixed(3)}%)</span></label>)}
                <ul className="list-disc ml-5">{selectedService.durationPrices.map((dp, i) => (<li key={i} className="mb-1">{dp.label ? `${dp.label}   |   ` : ''}{dp.duration} min   |   ${dp.price ? ((includeSalesTax && isCategoryTaxable(categories, selectedService.category)) ? (dp.price * (1 + appSalesTaxRate)).toFixed(2) : dp.price.toFixed(2)) : 'N/A'}</li>))}</ul>
              </div>)}

            {activePractitioners.filter(p => p.offered_services?.some(os => os.serviceName === selectedService.name)).length > 0 && (
              <div className="mt-4 pt-3 pb-2 px-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-3">Practitioners Offering This Service:</h4>
                <div>
                  {activePractitioners.filter(p => p.offered_services?.some(os => os.serviceName === selectedService.name)).map((prac, i) => {
                    const offeredSvcEntry = prac.offered_services.find(os => os.serviceName === selectedService.name);
                    return (
                      <div key={i} className="p-2 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors border-b border-gray-200 last:border-b-0" onClick={() => { playClickSound(); setSelectedPractitioner(prac); setActivePracTab('bio'); }}>
                        <div className="flex justify-between items-center">
                          <strong className="font-semibold">{prac.name}</strong>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {validOfferedDurations(offeredSvcEntry, selectedService).map(d => (
                              <span key={d} className="text-xs bg-gray-200 text-gray-700 font-medium px-2 py-0.5 rounded-full">{d}min</span>
                            ))}
                          </div>
                        </div>
                        {offeredSvcEntry?.specialNote && (<p className="text-red-600 italic mt-1 text-xs pl-1">{offeredSvcEntry.specialNote}</p>)}
                      </div>
                    );
                  })}
                </div>
              </div>)}
          </div>
        </div>)}
      </>)}
    </div>
  );
}
